import { HttpClient } from "@angular/common/http";
import { computed, inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { DeviceService, PropertyService, RoomService } from "@core/services";
import { environment } from "@environments/environment";
import { AuthState, LoginRequest, LoginResponse, User } from "@shared/models";
import { catchError, Observable, switchMap, tap, throwError } from "rxjs";

/**
 * Local storage key for the persisted bearer token.
 */
const AUTH_TOKEN_KEY = "mobile_cms_token";

/**
 * Local storage key for the persisted authenticated user profile.
 */
const AUTH_USER_KEY = "mobile_cms_user";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  /** Angular router used for auth-based navigation. */
  private readonly router = inject(Router);
  /** HTTP client used for authentication and profile requests. */
  private readonly http = inject(HttpClient);
  /** Device state service cleared on logout. */
  private readonly deviceService = inject(DeviceService);
  /** Room state service cleared on logout. */
  private readonly roomService = inject(RoomService);
  /** Property state service cleared on logout. */
  private readonly propertyService = inject(PropertyService);

  /**
   * Internal authentication state signal.
   * Initialized from local storage to restore persisted sessions.
   */
  private state = signal<AuthState>({
    user: this.getStoredUser(),
    token: this.getStoredToken(),
    isAuthenticated: !!this.getStoredToken(),
    loading: false,
    error: null,
  });

  /** Read-only computed signal with the current authenticated user, if available. */
  readonly user = computed(() => this.state().user);
  /** Read-only computed signal with the current access token, if available. */
  readonly token = computed(() => this.state().token);
  /** Read-only computed signal indicating whether a session is authenticated. */
  readonly isAuthenticated = computed(() => this.state().isAuthenticated);
  /** Read-only computed signal indicating whether an auth operation is in progress. */
  readonly loading = computed(() => this.state().loading);
  /** Read-only computed signal with the current authentication error message. */
  readonly error = computed(() => this.state().error);

  /**
   * Authenticates a user with OAuth-style credentials.
   * Persists the access token, fetches the current user profile, and updates auth state.
   *
   * @param credentials Login payload used by the token endpoint.
   * @returns An observable that emits the authenticated user profile.
   */
  login(credentials: LoginRequest): Observable<User> {
    this.state.update((s) => ({ ...s, loading: true, error: null }));

    return this.http
      .post<LoginResponse>(`${environment.authUrl}`, this.tokenUrlSearchParams(credentials), {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      })
      .pipe(
        tap((response) => {
          this.setSession(response);
          this.state.update((s) => ({
            ...s,
            token: response.access_token,
            isAuthenticated: true,
          }));
        }),
        switchMap(() => this.getUser()),
        tap((user) => {
          const userObj = new User(user);
          this.setSession(undefined, userObj);
          this.state.update((s) => ({
            ...s,
            user: userObj,
            loading: false,
          }));
        }),
        catchError((error) => {
          this.state.update((s) => ({
            ...s,
            loading: false,
            error: error.error?.message || "Login failed",
          }));
          return throwError(() => error);
        })
      );
  }

  /**
   * Clears persisted auth/session data and navigates the user to the login page.
   */
  logout(): void {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    this.state.set({
      user: null,
      token: null,
      isAuthenticated: false,
      loading: false,
      error: null,
    });
    this.roomService.clearRooms();
    this.deviceService.clearDevices();
    this.propertyService.clearProperties();
    this.router.navigate(["/login"]);
  }

  /**
   * Retrieves the authenticated user's profile from the API.
   *
   * @returns An observable that emits the current user profile.
   */
  getUser(): Observable<User> {
    return this.http.get<User>(`${environment.apiUrl}/v1/Users/Me`);
  }

  /**
   * Persists token and/or user data in local storage.
   *
   * @param response Optional token endpoint response containing an access token.
   * @param user Optional user profile to persist.
   */
  private setSession(response?: LoginResponse, user?: User): void {
    if (response?.access_token) localStorage.setItem(AUTH_TOKEN_KEY, response.access_token);
    if (user) localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  }

  /**
   * Reads the persisted access token from local storage.
   *
   * @returns The token string when available; otherwise null.
   */
  private getStoredToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(AUTH_TOKEN_KEY);
  }

  /**
   * Reads the persisted user profile from local storage.
   *
   * @returns The deserialized user object when available; otherwise null.
   */
  private getStoredUser(): User | null {
    if (typeof window === "undefined") return null;
    const stored = localStorage.getItem(AUTH_USER_KEY);
    return stored ? JSON.parse(stored) : null;
  }

  /**
   * Clears the current authentication error from state.
   */
  clearError(): void {
    this.state.update((s) => ({ ...s, error: null }));
  }

  /**
   * Converts login credentials into URL-encoded body params for the token endpoint.
   *
   * @param credentials Login payload.
   * @returns URLSearchParams representation of the token request body.
   */
  private tokenUrlSearchParams(credentials: LoginRequest): URLSearchParams {
    let urlencoded = new URLSearchParams();
    this.createTokenFormData(credentials).forEach((value, key) => {
      urlencoded.append(key, value.toString());
    });
    return urlencoded;
  }

  /**
   * Creates a form-data representation of the token request payload.
   * Applies fallback values for optional OAuth fields.
   *
   * @param credentials Login payload.
   * @returns FormData with token endpoint fields.
   */
  private createTokenFormData(credentials: LoginRequest): FormData {
    const formData = new FormData();
    formData.append("username", credentials.username);
    formData.append("password", credentials.password);
    formData.append("client_id", credentials.client_id);
    formData.append("client_secret", credentials.client_secret);
    formData.append("grant_type", credentials.grant_type || "password");
    formData.append("scope", credentials.scope || "connect");
    return formData;
  }
}
